import { ControlEventTypes } from "Core/Events/EventTypes";
import { INumberControl } from "Controls/Interfaces";
import { Control } from "Controls/Control";
import { Util } from "Core";

/**
 * ## Number
 *
 * Control for entering numbers with optional decimals.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a NumberControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class NumberControl extends Control implements INumberControl {
    private _kendoElement: kendo.ui.NumericTextBox;
    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-number";
    /**
     * @hidden
     */
    protected defaultEditorElement: string = "<input title=' ' />"; // set a empty title, otherwise the kendo element writes the current value as title.

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("focusout", e => this.trigger(ControlEventTypes.LostFocus));

        this._kendoElement = new kendo.ui.NumericTextBox(this.jQueryElement[0], {
            format: this.getFormat(),
            spinners: false,
            decimals: this.controlDefinition.DecimalDigits || 0,
            max: this.controlDefinition.MaxValue,
            min: this.controlDefinition.MinValue,
            change: e => this.trigger(ControlEventTypes.Change)
        });

        this._kendoElement.wrapper.on("focus", ".k-input", () => {
            setTimeout(() => this._kendoElement.wrapper.find(".k-input").select());
        });
    }

    /**
     * Get the display template for grid cells.
     * @param bindingField Name of field.
     * @hidden
     */
    protected getDisplayTemplate(bindingField?: string): string {
        let format = this.getFormat()
            .split("#")
            .join("\\#");
        format = format.replace("\\%", "\\\\%"); // display needs further escape
        if (this.controlDefinition.EncodeHtml) {
            return `#= ${bindingField} == null ? "${this.controlDefinition.ReplaceNullValue ||
                ""}" : DMS.Util.encode(kendo.toString(${bindingField}, "${format}"), DMS.Enums.EncodingTypes.Html)#`;
        } else {
            return `#= ${bindingField} == null ? "${this.controlDefinition.ReplaceNullValue ||
                ""}" : kendo.toString(${bindingField}, "${format}") #`;
        }
    }

    /**
     * Gets the format string.
     * @hidden
     */
    public getFormat(): string {
        let format = Util.getNumberFormat(
            this.controlDefinition.GroupSeparator,
            this.controlDefinition.DecimalDigits,
            this.controlDefinition.DisplayAsPercent
        );
        return format;
    }

    /**
     * Set the minimum allowed number for this control.
     * @param min Minimum allowed number.
     * @since 4.0.0
     */
    public setMin(min: number) {
        this._kendoElement.min(min);
    }

    /**
     * Get the minimum allowed number for this control.
     * @since 4.0.0
     */
    public getMin(): number {
        return this._kendoElement.min();
    }

    /**
     * Set the maximum allowed number for this control.
     * @param max Maximum allowed number.
     * @since 4.0.0
     */
    public setMax(max: number) {
        this._kendoElement.max(max);
    }

    /**
     * Get the maximum allowed number for this control.
     * @since 4.0.0
     */
    public getMax(): number {
        return this._kendoElement.max();
    }

    /**
     * Get the current number value.
     * @since 4.0.0
     */
    public getValue(): number {
        return this._kendoElement.value();
    }

    /**
     * Set the number value.
     * @param value Number value to be set.
     * @since 4.0.0
     */
    public setValue(value: number): void {
        let oldValue = this.getValue();
        if (oldValue !== value) {
            this._kendoElement.value(value);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Enable the control.
     * @since 4.0.0
     */
    public enable(): void {
        this._kendoElement.enable(true);
    }

    /**
     * Disable the control.
     * @since 4.0.0
     */
    public disable(): void {
        this._kendoElement.enable(false);
    }

    /**
     * Gets the datatype for the ViewModel (ie. "string", "number", etc.)
     * @hidden
     */
    public getModelDatatype(): string {
        return "number";
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     * @hidden
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement != null) {
            this._kendoElement.destroy();
        }
        if (this.jQueryElement != null) {
            this.jQueryElement.off("focusout");
        }
    }
}
